'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Employee, EmployeeFullReview } from './types';

interface ReviewModalProps {
  employee: Employee | null;
  existingReview: EmployeeFullReview | null;
  onClose: () => void;
  onSaved: () => void;
}

const REVIEW_TYPES = [
  { key: 'annual',        label: 'Annual Review' },
  { key: 'mid_year',      label: 'Mid-Year Review' },
  { key: 'quarterly',     label: 'Quarterly Review' },
  { key: '30_day',        label: '30-Day Review' },
  { key: '60_day',        label: '60-Day Review' },
  { key: '90_day',        label: '90-Day Review' },
  { key: 'probationary',  label: 'Probationary' },
  { key: 'project_based', label: 'Project-Based' },
  { key: 'disciplinary',  label: 'Disciplinary' },
  { key: 'other',         label: 'Other' },
];

const RATING_DIMENSIONS = [
  { key: 'rating_communication',   label: 'Communication',   desc: 'Clarity, listening, written + verbal' },
  { key: 'rating_quality_of_work', label: 'Quality of Work', desc: 'Accuracy, attention to detail, output' },
  { key: 'rating_reliability',     label: 'Reliability',     desc: 'On-time, follows through, dependable' },
  { key: 'rating_teamwork',        label: 'Teamwork',        desc: 'Collaboration, supports peers, respect' },
  { key: 'rating_initiative',      label: 'Initiative',      desc: 'Proactive, problem-solving, ownership' },
  { key: 'rating_safety',          label: 'Safety',          desc: 'OSHA compliance, hazard awareness' },
] as const;

type RatingKey = typeof RATING_DIMENSIONS[number]['key'];
type ReviewTab = 'ratings' | 'feedback' | 'goals';

const ReviewModal = ({ employee, existingReview, onClose, onSaved }: ReviewModalProps) => {
  const isEdit = !!existingReview;
  const isLocked = isEdit && existingReview!.status !== 'draft';

  const [activeTab, setActiveTab] = useState<ReviewTab>('ratings');
  const [reviewType, setReviewType] = useState<string>('annual');
  const [periodStart, setPeriodStart] = useState<string>('');
  const [periodEnd, setPeriodEnd] = useState<string>('');
  const [reviewerName, setReviewerName] = useState<string>('');
  const [overallRating, setOverallRating] = useState<number>(4.0);
  const [ratings, setRatings] = useState<Record<RatingKey, number>>({
    rating_communication: 4.0,
    rating_quality_of_work: 4.0,
    rating_reliability: 4.0,
    rating_teamwork: 4.0,
    rating_initiative: 4.0,
    rating_safety: 4.0,
  });
  const [strengths, setStrengths] = useState<string>('');
  const [areasForImprovement, setAreasForImprovement] = useState<string>('');
  const [goalsNextPeriod, setGoalsNextPeriod] = useState<string>('');
  const [reviewerComments, setReviewerComments] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();
  const { currentUser } = useAuth();

  // Reset / hydrate when modal opens
  useEffect(() => {
    if (!employee) return;
    setActiveTab('ratings');
    setSubmitting(false);
    if (existingReview) {
      setReviewType(existingReview.review_type ?? 'annual');
      setPeriodStart(existingReview.review_period_start?.slice(0, 10) ?? '');
      setPeriodEnd(existingReview.review_period_end?.slice(0, 10) ?? '');
      setReviewerName(existingReview.reviewer_name ?? '');
      setOverallRating(existingReview.overall_rating ?? 4.0);
      setRatings({
        rating_communication: existingReview.rating_communication ?? 4.0,
        rating_quality_of_work: existingReview.rating_quality_of_work ?? 4.0,
        rating_reliability: existingReview.rating_reliability ?? 4.0,
        rating_teamwork: existingReview.rating_teamwork ?? 4.0,
        rating_initiative: existingReview.rating_initiative ?? 4.0,
        rating_safety: existingReview.rating_safety ?? 4.0,
      });
      setStrengths(existingReview.strengths ?? '');
      setAreasForImprovement(existingReview.areas_for_improvement ?? '');
      setGoalsNextPeriod(existingReview.goals_next_period ?? '');
      setReviewerComments(existingReview.reviewer_comments ?? '');
    } else {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      setReviewType('annual');
      setPeriodStart(start.toISOString().slice(0, 10));
      setPeriodEnd(now.toISOString().slice(0, 10));
      setReviewerName(`${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim());
      setOverallRating(4.0);
      setRatings({
        rating_communication: 4.0,
        rating_quality_of_work: 4.0,
        rating_reliability: 4.0,
        rating_teamwork: 4.0,
        rating_initiative: 4.0,
        rating_safety: 4.0,
      });
      setStrengths('');
      setAreasForImprovement('');
      setGoalsNextPeriod('');
      setReviewerComments('');
    }
  }, [employee?.id, existingReview?.id, currentUser]);

  // Auto-recompute overall rating as average of dimensions
  useEffect(() => {
    const values = Object.values(ratings);
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    setOverallRating(Math.round(avg * 2) / 2);
  }, [ratings]);

  // Close on Escape
  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, onClose]);

  if (!employee) return null;

  const isValid =
    !!periodStart && !!periodEnd && !!reviewType && reviewerName.trim().length >= 2 && !submitting;

  const buildPayload = (statusValue: 'draft' | 'released') => {
    const now = new Date().toISOString();
    // Don't set reviewer_id from auth — currentUser.id points to user_profiles.id,
    // not employees.id. The reviewer_id FK column REFERENCES employees.id, so
    // setting an unmatched UUID causes the insert to hang. Leave reviewer_id null;
    // reviewer_name carries the displayable info. We can populate reviewer_id
    // later if/when user_profiles is properly linked to employees via the
    // employee_id FK that HR-1 added.
    const reporterId: string | null = null;
    const payload: any = {
      employee_id: employee.id,
      reviewer_id: reporterId,
      reviewer_name: reviewerName.trim(),
      review_period_start: periodStart,
      review_period_end: periodEnd,
      review_type: reviewType,
      overall_rating: overallRating,
      ...ratings,
      strengths: strengths.trim() || null,
      areas_for_improvement: areasForImprovement.trim() || null,
      goals_next_period: goalsNextPeriod.trim() || null,
      reviewer_comments: reviewerComments.trim() || null,
      status: statusValue,
      updated_at: now,
    };
    if (statusValue === 'released' && (!isEdit || existingReview!.status === 'draft')) {
      payload.released_at = now;
    }
    return payload;
  };

  const saveWithRetry = async (statusValue: 'draft' | 'released'): Promise<void> => {
    if (!isValid) return;
    setSubmitting(true);

    const payload = buildPayload(statusValue);
    console.log(`[ReviewModal] Target employee_id: ${employee.id} (employee table)`);
    console.log(`[ReviewModal] Saving as ${statusValue}. Full payload below:`);
    console.log('[ReviewModal] payload JSON:', JSON.stringify(payload, null, 2));
    console.log('[ReviewModal] payload keys:', Object.keys(payload));
    console.log('[ReviewModal] payload values:', Object.entries(payload).map(([k, v]) => `${k}: ${typeof v} = ${JSON.stringify(v)}`));

    console.log('[ReviewModal] Testing Supabase connectivity from browser...');
    try {
      const testStart = Date.now();
      const testResult = await Promise.race([
        supabase.from('employees').select('id').eq('id', employee.id).single(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Connectivity test timed out')), 5000)),
      ]);
      console.log(`[ReviewModal] Connectivity test passed in ${Date.now() - testStart}ms:`, testResult);
    } catch (testErr) {
      console.error('[ReviewModal] Connectivity test FAILED:', testErr);
    }

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000)
      );

      let opPromise: Promise<any>;
      if (isEdit) {
        opPromise = supabase
          .from('employee_reviews')
          .update(payload)
          .eq('id', existingReview!.id)
          .select()
          .single()
          .then((res: any) => { if (res.error) throw res.error; return res.data; });
      } else {
        opPromise = supabase
          .from('employee_reviews')
          .insert(payload)
          .select()
          .single()
          .then((res: any) => { if (res.error) throw res.error; return res.data; });
      }

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[ReviewModal] Save succeeded attempt ${attempt + 1}`);
        toast.success(
          statusValue === 'released'
            ? `Review released — ${employee.first_name} can now see it`
            : 'Review saved as draft',
          { duration: 3500, icon: statusValue === 'released' ? '🚀' : '📝' }
        );
        onSaved();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[ReviewModal] Attempt ${attempt + 1} failed: ${msg}`);
        console.warn('[ReviewModal] Full error object:', err);
        if (err && typeof err === 'object') {
          console.warn('[ReviewModal] error.code:', (err as any).code);
          console.warn('[ReviewModal] error.message:', (err as any).message);
          console.warn('[ReviewModal] error.details:', (err as any).details);
          console.warn('[ReviewModal] error.hint:', (err as any).hint);
        }
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Save failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-modal-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 id="review-modal-title" className="text-lg font-bold text-[#1C2E5E]">
                {isEdit ? (isLocked ? 'View Review' : 'Edit Draft Review') : 'New Performance Review'}
              </h3>
              {isLocked && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-gray-200 text-gray-700">
                  {existingReview!.status.toUpperCase()} · LOCKED
                </span>
              )}
            </div>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {employee.first_name} {employee.last_name} · {employee.job_title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 border-b border-gray-100">
          {[
            { key: 'ratings',  label: 'Ratings' },
            { key: 'feedback', label: 'Feedback' },
            { key: 'goals',    label: 'Goals & Comments' },
          ].map(tab => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key as ReviewTab)}
                className={`relative px-3 py-3 text-[12px] font-semibold transition-colors ${
                  active ? 'text-[#E89A3C]' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E89A3C]" />}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'ratings' && (
            <div className="space-y-4">
              {/* Period + Type + Reviewer */}
              <div className="grid grid-cols-2 gap-3 pb-4 border-b border-gray-100">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Period Start <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={periodStart}
                    onChange={e => setPeriodStart(e.target.value)}
                    disabled={isLocked}
                    className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Period End <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={periodEnd}
                    onChange={e => setPeriodEnd(e.target.value)}
                    disabled={isLocked}
                    className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={reviewType}
                    onChange={e => setReviewType(e.target.value)}
                    disabled={isLocked}
                    className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60"
                  >
                    {REVIEW_TYPES.map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Reviewer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={reviewerName}
                    onChange={e => setReviewerName(e.target.value)}
                    disabled={isLocked}
                    placeholder="Mike Chesbrough"
                    className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60"
                  />
                </div>
              </div>

              {/* Overall rating display */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-[#E89A3C]/10 to-[#1C2E5E]/10">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Overall Rating (auto-computed)
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[#1C2E5E]">{overallRating.toFixed(1)}</span>
                  <span className="text-[12px] text-gray-500">/ 5.0</span>
                  <div className="flex items-center gap-0.5 ml-2">
                    {[1, 2, 3, 4, 5].map(i => (
                      <svg
                        key={i}
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill={
                          overallRating >= i
                            ? '#F5A623'
                            : overallRating >= i - 0.5
                            ? 'url(#halfg)'
                            : '#e5e7eb'
                        }
                        aria-hidden="true"
                      >
                        <defs>
                          <linearGradient id="halfg">
                            <stop offset="50%" stopColor="#F5A623" />
                            <stop offset="50%" stopColor="#e5e7eb" />
                          </linearGradient>
                        </defs>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sectional ratings — 6 sliders */}
              <div className="space-y-3">
                {RATING_DIMENSIONS.map(dim => {
                  const value = ratings[dim.key];
                  return (
                    <div key={dim.key} className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-bold text-[#1C2E5E]">{dim.label}</p>
                          <p className="text-[10px] text-gray-500">{dim.desc}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-lg font-bold text-[#E89A3C]">{value.toFixed(1)}</span>
                          <span className="text-[10px] text-gray-500"> / 5</span>
                        </div>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        step="0.5"
                        value={value}
                        onChange={e =>
                          setRatings(prev => ({ ...prev, [dim.key]: Number(e.target.value) }))
                        }
                        disabled={isLocked}
                        className="w-full disabled:opacity-60"
                      />
                      <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                        <span>Needs Improvement</span>
                        <span>Meets</span>
                        <span>Exceeds</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-green-700 mb-1.5">
                  Strengths
                </label>
                <textarea
                  value={strengths}
                  onChange={e => setStrengths(e.target.value)}
                  disabled={isLocked}
                  rows={5}
                  maxLength={2000}
                  placeholder="What this employee does exceptionally well…"
                  className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60 resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">{strengths.length}/2000</p>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1.5">
                  Areas for Improvement
                </label>
                <textarea
                  value={areasForImprovement}
                  onChange={e => setAreasForImprovement(e.target.value)}
                  disabled={isLocked}
                  rows={5}
                  maxLength={2000}
                  placeholder="Specific behaviors or skills to develop…"
                  className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60 resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">{areasForImprovement.length}/2000</p>
              </div>
            </div>
          )}

          {activeTab === 'goals' && (
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-blue-700 mb-1.5">
                  Goals for Next Period
                </label>
                <textarea
                  value={goalsNextPeriod}
                  onChange={e => setGoalsNextPeriod(e.target.value)}
                  disabled={isLocked}
                  rows={5}
                  maxLength={2000}
                  placeholder="Specific, measurable goals to focus on next quarter/year…"
                  className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60 resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">{goalsNextPeriod.length}/2000</p>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Reviewer&apos;s Closing Comments
                </label>
                <textarea
                  value={reviewerComments}
                  onChange={e => setReviewerComments(e.target.value)}
                  disabled={isLocked}
                  rows={4}
                  maxLength={2000}
                  placeholder="Any additional context or observations…"
                  className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 disabled:opacity-60 resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">{reviewerComments.length}/2000</p>
              </div>
              {isEdit && existingReview!.employee_comments && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">
                    Employee&apos;s Acknowledgment Comments
                  </p>
                  <p className="text-[12px] text-blue-900 leading-relaxed">
                    {existingReview!.employee_comments}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-5 border-t border-gray-100">
          {isLocked ? (
            <p className="text-[11px] text-gray-500 italic">
              This review is {existingReview!.status} and cannot be edited.
            </p>
          ) : (
            <p className="text-[11px] text-gray-500">
              {isEdit ? 'Editing draft' : 'New review'} · Save as draft to keep working, or release
              to send to {employee.first_name}.
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
            >
              {isLocked ? 'Close' : 'Cancel'}
            </button>
            {!isLocked && (
              <>
                <button
                  type="button"
                  onClick={() => saveWithRetry('draft')}
                  disabled={!isValid}
                  className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      !confirm(
                        `Release this review to ${employee.first_name}? Once released, it cannot be edited.`
                      )
                    )
                      return;
                    saveWithRetry('released');
                  }}
                  disabled={!isValid}
                  className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#E89A3C] hover:bg-[#0891c9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? 'Saving…' : 'Release Now'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
